/**
 * oRPC client factory for the Office Add-in.
 *
 * The client uses RPCLink to call the DocuID web app's /api/rpc endpoint.
 * Authentication is handled via the session cookie (credentials: 'include')
 * which is set by the web app after biometric login.
 *
 * ORPC_BASE_URL is injected at build time via webpack DefinePlugin.
 * - Development: https://localhost:3001 (dashboard dev server)
 * - Production:  https://dashboard.docuid.net
 *
 * Type safety note:
 * The add-in runs in a Webpack/Babel (ES5) bundle. The TypeScript compiler
 * is only used for type-checking (`tsc --noEmit`); Babel does the actual
 * compilation. We use a lightweight client interface that mirrors the
 * AppRouter shape without importing the full server-only dependency chain.
 */

import { createORPCClient } from '@orpc/client';
import { RPCLink } from '@orpc/client/fetch';

import { getDashboardBaseUrl } from './config';

function getRpcUrl(): string {
  return `${getDashboardBaseUrl()}/api/rpc`;
}

// Dispatched when any request returns 401 so the App shell can react
// (clear UI state, redirect to login, surface "Session expired").
export const UNAUTHORIZED_EVENT = 'docuid-unauthorized';

function notifyUnauthorized(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

const link = new RPCLink({
  url: getRpcUrl(),
  fetch: async (input, init) => {
    const response = await fetch(input, {
      ...init,
      credentials: 'include',
    });
    if (response.status === 401) {
      notifyUnauthorized();
    }
    return response;
  },
});

// Lightweight interface that mirrors the oRPC router shape used by the add-in.
// This avoids importing the full server-only dependency chain while keeping
// method calls typed at the service layer.
export interface AddinRouterShape {
  auth: {
    requestBiometric: (input: {
      mobile: string;
      clientPublicKey: string;
    }) => Promise<{ status: 'sent'; requestId: string } | { status: 'not_found' }>;
    pollBiometricResult: (input: { mobile: string }) => Promise<{
      status: 'pending' | 'success' | 'denied' | 'not_found';
      user?: { id: string; name: string; email: string; mobileNumber: string | null };
      encryptedVaultPayload?: string;
    }>;
    createSession: (input: {
      mobile: string;
      ivaltUserId?: string;
      encryptedVaultPayload?: string;
    }) => Promise<{
      user: { id: string; name: string; email: string; mobileNumber: string | null };
    }>;
    logout: (input: Record<string, never>) => Promise<{ success: true }>;
  };
  documents: {
    list: (input: {
      folderId?: string | null;
      workspaceId?: string | null;
      filters?: { mimeType?: string; status?: 'active' | 'archived' | 'deleted' };
      sort?: { field?: 'name' | 'date' | 'size'; direction?: 'asc' | 'desc' };
      cursor?: string;
      limit?: number;
    }) => Promise<{
      items: {
        id: string;
        fileName: string;
        mimeType: string;
        fileSizeBytes: number;
        description: string | null;
        folderId: string | null;
        workspaceId: string | null;
        isPasswordProtected: boolean;
        storageKey: string;
        status: 'active' | 'archived' | 'deleted';
        createdAt: Date;
        updatedAt: Date;
      }[];
      nextCursor: string | null;
    }>;
    get: (input: { id: string }) => Promise<{
      id: string;
      fileName: string;
      mimeType: string;
      fileSizeBytes: number;
      description: string | null;
      folderId: string | null;
      workspaceId: string | null;
      isPasswordProtected: boolean;
      storageKey: string;
      status: 'active' | 'archived' | 'deleted';
      createdAt: Date;
      updatedAt: Date;
    }>;
  };
  shares: {
    createExternal: (input: {
      documentId: string;
      recipientEmail?: string;
      recipientMobile?: string;
      accessLevel: 'view' | 'download' | 'edit';
      wrappedDek: string;
      recipientKeyVersion?: string;
      message?: string;
      expiresAt?: Date;
      accessLimit?: number;
      password?: string;
    }) => Promise<{
      id: string;
      shareLink?: string;
      message?: string;
      shareToken: string;
    }>;
  };
  encryptionKeys: {
    getMine: () => Promise<{
      encryptedPrivateKey: string;
      privateKeyIv: string;
      privateKeySalt: string;
      publicKeyPem: string;
      keyVersion: string;
    }>;
    getDocumentKey: (input: { documentId: string }) => Promise<{
      wrappedDek: string;
      fileIv: string;
      model: 'client_rsa' | 'legacy' | 'none';
      ownerKeyVersion: string | null;
    }>;
  };
  workspaces: {
    list: () => Promise<{ id: string; name: string }[]>;
  };
  folders: {
    list: (input: {
      workspaceId?: string;
      parentId?: string | null;
    }) => Promise<{ id: string; name: string }[]>;
  };
  uploads: {
    createSession: (input: {
      fileName: string;
      size: number;
      mimeType: string;
      folderId?: string;
      documentId?: string;
      workspaceId?: string | null;
      encryption: { fileIv: string; wrappedDek: string };
    }) => Promise<{ sessionId: string; uploadUrl: string }>;
    completeSession: (input: { sessionId: string }) => Promise<{ id: string }>;
  };
}

export const addinClient = createORPCClient(link) as unknown as AddinRouterShape;
